import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import * as echarts from 'echarts';
import { InjectionMap } from 'src/app/model/response/file-map-response';
import {
  GridQuantity,
  OrientedGrid,
  loadAxisLabel,
  orientInjectionGrid,
  orientNativeCurve,
} from 'src/app/model/injection-map-orientation';
import { mapAxisText, mapCellText } from 'src/app/model/injection-map-format';

const JET = ['#000080', '#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff7f00', '#ff0000', '#7f0000'];

/**
 * Heatmap do mapa de injeção em grade categórica.
 *
 * A orientação dos eixos NÃO é fixa: vem dos flags `inverted_axis`/`inverted_rows`/
 * `inverted_cols` do payload, aplicados na ordem da Seção 3.2 do contrato
 * (`orientInjectionGrid`). No caso de fábrica do Vision o resultado é carga na vertical
 * (maior valor no topo) e RPM na horizontal; PowerFT (flags false) fica RPM na vertical.
 *
 * Com `curve`, exibe a curva básica (`native_curve`, §3.2.1) como matriz de 1 linha × N
 * cargas, com os mesmos flags. A dimensão cuja grandeza é `'rpm'` é só estrutural (não é
 * 1 RPM nem 0 RPM): não tem nome, rótulo nem linha no tooltip.
 */
@Component({
  selector: 'app-injection-map-2d',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<div #chart class="chart-2d"></div>',
  styles: [`
    :host, .chart-2d { display: block; width: 100%; height: 100%; }
  `],
})
export class InjectionMap2dComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() map!: InjectionMap;
  /** Exibe a curva básica (`map.native_curve`) em vez da grade `values_ms`. */
  @Input() curve = false;
  @ViewChild('chart', { static: true }) chartEl!: ElementRef<HTMLDivElement>;

  private instance: echarts.ECharts | null = null;
  /** Índice da célula atualmente em highlight (no array `cells` da série heatmap). */
  private highlightedIndex: number | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private translocoService: TranslocoService) {}

  ngAfterViewInit(): void {
    this.instance = echarts.init(this.chartEl.nativeElement, 'dark');
    this.render();
    // ECharts mede o container no init e cacheia a largura. Se o flex do pai
    // ainda não finalizou o layout nesse momento (primeira renderização), o
    // chart fica subdimensionado. ResizeObserver dispara um resize() sempre
    // que o container muda de tamanho — corrige a primeira renderização e
    // quaisquer reflows futuros sem precisar de evento window:resize.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.instance?.resize());
      this.resizeObserver.observe(this.chartEl.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['map'] || changes['curve']) && this.instance) this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.instance?.dispose();
    this.instance = null;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.instance?.resize();
  }

  /** Forçar resize após mudança de layout externa (ex.: troca de aba). */
  resize(): void {
    this.instance?.resize();
  }

  /**
   * Destaca a célula mais próxima do par (loadValue, rpmValue) e mostra o
   * tooltip nativo do echart com RPM/load/ms. Usa `dispatchAction` (não
   * `setOption`) — não re-renderiza o heatmap, então os números das outras
   * células nunca somem/piscam.
   */
  setSite(loadValue: number, rpmValue: number): void {
    if (!this.instance || !this.map) return;
    const { rowLabels, colLabels, rowIs, colIs } = this.oriented();
    // Casa cada valor com o eixo que REALMENTE o representa (a orientação varia por arquivo).
    // Na curva, a dimensão de RPM é estrutural (1 posição): o índice é sempre 0.
    const rowIdx = this.curve && rowIs === 'rpm' ? 0 : this.nearestIndex(rowIs === 'rpm' ? rpmValue : loadValue, rowLabels);
    const colIdx = this.curve && colIs === 'rpm' ? 0 : this.nearestIndex(colIs === 'rpm' ? rpmValue : loadValue, colLabels);
    if (rowIdx < 0 || colIdx < 0) { this.clearSite(); return; }

    // `cells` foi montado varrendo row (leitura) e col: dataIndex = row * nCols + col.
    const dataIndex = rowIdx * colLabels.length + colIdx;
    if (dataIndex === this.highlightedIndex) return; // já está no mesmo lugar

    if (this.highlightedIndex !== null && this.highlightedIndex !== dataIndex) {
      this.instance.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: this.highlightedIndex });
    }
    this.instance.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex });
    this.instance.dispatchAction({ type: 'showTip',   seriesIndex: 0, dataIndex });
    this.highlightedIndex = dataIndex;
  }

  /** Remove o highlight e o tooltip (chamado no globalout do chart do log). */
  clearSite(): void {
    if (!this.instance) return;
    if (this.highlightedIndex !== null) {
      this.instance.dispatchAction({ type: 'downplay', seriesIndex: 0, dataIndex: this.highlightedIndex });
      this.highlightedIndex = null;
    }
    this.instance.dispatchAction({ type: 'hideTip' });
  }

  /** Índice do elemento de `arr` mais próximo de `value`. -1 se vazio. */
  private nearestIndex(value: number, arr: number[]): number {
    if (arr.length === 0) return -1;
    let bestIdx = 0;
    let bestDist = Math.abs(arr[0] - value);
    for (let i = 1; i < arr.length; i++) {
      const d = Math.abs(arr[i] - value);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    }
    return bestIdx;
  }

  private render(): void {
    if (!this.map || !this.instance) return;
    // O chart é recriado com notMerge: o highlight antigo deixa de existir.
    this.highlightedIndex = null;

    const { rowLabels, colLabels, grid, rowIs, colIs } = this.oriented();
    const nRows = rowLabels.length;

    // `rowLabels` vem em ordem de LEITURA (topo → base), mas o eixo categórico do ECharts
    // desenha o índice 0 na BASE. Então o eixo recebe a lista invertida e a célula usa
    // y = nRows-1-row. Assim a primeira linha exibida aparece no topo, como no contrato.
    const yAxisData = rowLabels.slice().reverse();

    const cells: [number, number, number][] = [];
    let min = Infinity;
    let max = -Infinity;
    for (let row = 0; row < nRows; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const v = grid[row][col];
        cells.push([col, nRows - 1 - row, v]);
        // Célula ausente (não finita) fica na série, mas não entra na escala de cor (D8).
        if (Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) { min = 0; max = 1; }
    if (min === max) max = min + 1;

    // Densidade afeta só o tamanho da fonte / espaçamento de labels dos eixos,
    // mas os valores DENTRO das células sempre aparecem.
    const total = rowLabels.length * colLabels.length;
    const dense = total > 1000;
    const cellFont = total > 1500 ? 7 : total > 900 ? 8 : total > 500 ? 9 : 10;
    const structural = (q: GridQuantity) => this.curve && q === 'rpm';
    const fmtLabel = (q: GridQuantity, v: number) =>
      structural(q) ? '' : mapAxisText(v);

    this.instance.setOption(
      {
        backgroundColor: 'transparent',
        tooltip: {
          formatter: (p: any) => {
            const colVal = colLabels[p.value[0]];
            const rowVal = yAxisData[p.value[1]];
            // Rotula pela grandeza real de cada eixo — não assume RPM na horizontal.
            const linhas = [
              ...(structural(colIs) ? [] : [`<b>${this.quantityLabel(colIs)}:</b> ${fmtLabel(colIs, colVal)}`]),
              ...(structural(rowIs) ? [] : [`<b>${this.quantityLabel(rowIs)}:</b> ${fmtLabel(rowIs, rowVal)}`]),
              `<b>${this.translocoService.translate('injectionMap.injectionTime')}:</b> ${this.fmt(p.value[2])} ${this.escapeUnit(this.map.units)}`,
            ];
            return linhas.join('<br/>');
          },
        },
        grid: { top: 12, right: 72, bottom: 38, left: 56 },
        xAxis: {
          type: 'category',
          name: structural(colIs) ? '' : this.quantityLabel(colIs),
          nameLocation: 'middle',
          nameGap: 26,
          data: colLabels.map((v) => fmtLabel(colIs, v)),
          axisLabel: {
            color: '#9b9b9b',
            rotate: dense ? 45 : 0,
            interval: dense ? Math.ceil(colLabels.length / 12) : 0,
            fontSize: 10,
          },
        },
        yAxis: {
          type: 'category',
          name: structural(rowIs) ? '' : this.quantityLabel(rowIs),
          nameLocation: 'middle',
          nameGap: 48,
          data: yAxisData.map((v) => fmtLabel(rowIs, v)),
          axisLabel: {
            color: '#9b9b9b',
            interval: dense ? Math.ceil(yAxisData.length / 12) : 0,
            fontSize: 10,
          },
        },
        visualMap: {
          min, max,
          calculable: true,
          orient: 'vertical',
          right: 6,
          top: 'middle',
          itemWidth: 10,
          itemHeight: 120,
          inRange: { color: JET },
          textStyle: { color: '#9b9b9b', fontSize: 10 },
          // Rótulos dos extremos cortados em 3 casas (D1), não o arredondamento do ECharts.
          formatter: (v: number) => mapCellText(Number(v)),
          dimension: 2,
          seriesIndex: 0,
        },
        series: [
          {
            type: 'heatmap',
            data: cells,
            label: {
              show: true,
              color: '#fff',
              fontFamily: 'Consolas, monospace',
              fontSize: cellFont,
              textBorderColor: '#121212',
              textBorderWidth: 2,
              formatter: (p: any) => this.fmt(p.value[2]),
            },
            // Emphasis = visual do "site cursor" disparado por dispatchAction('highlight').
            // Borda branca espessa + sombra → mesmo efeito do screenshot do usuário.
            emphasis: {
              itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 2,
                shadowBlur: 10,
                shadowColor: 'rgba(255, 255, 255, 0.7)',
              },
              label: {
                show: true,
                color: '#ffffff',
                fontFamily: 'Consolas, monospace',
                fontSize: Math.max(cellFont + 1, 10),
                fontWeight: 'bold',
                textBorderColor: '#121212',
                textBorderWidth: 2,
              },
            },
          },
        ],
      },
      true,
    );
  }

  /**
   * Grade EXIBIDA, conforme a Seção 3.2 do contrato — ver
   * `orientInjectionGrid()` em `src/app/model/injection-map-orientation.ts`.
   *
   * Substituiu a heurística anterior (que condicionava os flags a "eixo
   * ascendente" e reordenava a matriz sem o eixo). Aquela versão espelhava os
   * valores: no exemplo do contrato (§3.2), (RPM 800, load 1.0) devolvia 4.7
   * quando o correto é 4.2.
   */
  private oriented(): OrientedGrid {
    const map = this.map;
    return this.curve && map.native_curve
      ? orientNativeCurve({ ...map, native_curve: map.native_curve })
      : orientInjectionGrid(map);
  }

  /** Rótulo da grandeza de um eixo exibido (para eixo/tooltip). */
  private quantityLabel(q: GridQuantity): string {
    return q === 'rpm' ? 'RPM' : loadAxisLabel(this.map.axis);
  }

  private escapeUnit(s: string): string {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Tempo de injeção em ms, com 3 casas cortadas (igual aos editores). */
  private fmt(v: number): string {
    return mapCellText(v);
  }
}
